import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Deleted brands panel — audit-trail powered restore.
 *
 * Lists every row in `deleted_brands` that hasn't been restored yet.
 * Restore reinserts the full snapshot (brand + profile + versions +
 * pipeline_runs + approval_tasks + changelog) via the backend
 * `/api/admin/deleted-brands/{brand_id}/restore` endpoint.
 */
export default function AdminDeletedBrands() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    http.get("/admin/deleted-brands?limit=100")
      .then((r) => setRows(r.data.entries || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); }, []);

  const restore = async (row) => {
    if (!window.confirm(
      `Restore "${row.display_name || row.slug}"? This reinserts the brand ` +
      `and every related document (profile, versions, runs, tasks, changelog).`,
    )) return;
    setBusy(row.brand_id);
    try {
      const { data } = await http.post(`/admin/deleted-brands/${row.brand_id}/restore`);
      toast.success(
        `Restored ${row.slug} — brand=${data.restored.brand}, ` +
        `profile=${data.restored.profile}, versions=${data.restored.profile_versions}`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setBusy(null); }
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <h1 className="font-serif text-4xl mb-6">Deleted brands</h1>
        <p className="text-sm mb-6" style={{ color: "var(--bp-text-secondary)" }}>
          Every deletion is snapshotted before being removed. Restore reinserts the brand and
          every related document exactly as it was. Restored entries drop out of this list.
        </p>
        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Deleted</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">By</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest">Cascade</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={5} className="px-4 py-6 text-center" style={{ color: "var(--bp-text-muted)" }}>Loading…</td></tr>}
              {rows?.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center" style={{ color: "var(--bp-text-muted)" }}>
                  No deleted brands. Nothing to restore.
                </td></tr>
              )}
              {(rows || []).map((r) => (
                <tr key={r.brand_id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`deleted-row-${r.slug}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(r.deleted_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    {r.display_name}
                    <span className="text-xs font-mono ml-1" style={{ color: "var(--bp-text-muted)" }}>· {r.sector_code}</span>
                    <div className="text-[10px] font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.deleted_by_email || <span style={{ color: "var(--bp-text-muted)" }}>—</span>}</td>
                  <td className="px-4 py-3 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
                    p={r.delete_counts?.profile ?? 0} · v={r.delete_counts?.profile_versions ?? 0} · r={r.delete_counts?.pipeline_runs ?? 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      onClick={() => restore(r)}
                      disabled={busy === r.brand_id}
                      variant="outline"
                      className="rounded-none h-8 text-xs"
                      data-testid={`restore-${r.slug}`}
                    >
                      {busy === r.brand_id ? "Restoring…" : "Restore →"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 text-xs">
          <Link to="/admin/brands" className="bp-link">← Back to brands</Link>
        </div>
      </div>
    </AdminLayout>
  );
}
